import React, {useState} from 'react'
import {Helmet} from 'react-helmet-async'
import {AccessDenied} from '../../../components/errors'
import {BetaFlagMarker} from '../../../components/feature-flags'
import {ButtonGroup} from '../../../components/form'
import {ElapsedAbsoluteTime} from '../../../components/formatters'
import {Button} from '../../../components/library/buttons'
import {
  DropdownBody,
  DropdownFooter,
  DropdownItem
} from '../../../components/library/dropdown/content'
import {Icon} from '../../../components/library/icon/icon'
import {
  List,
  ListColumns,
  NoResults
} from '../../../components/library/list/index'
import {Modal} from '../../../components/library/modal/modal'
import {Loader} from '../../../components/loader'
import {OverflowFooter} from '../../../components/overflow'
import {useCustomEvents} from '../../../hooks/use-custom-events'
import {CustomEventConfigsDetailFragment} from '../../../hooks/generated/use-custom-events'
import {
  PermissionAction,
  PermissionResource
} from '../../../data/workbench-api/generated/types'
import {EnvironmentNode} from '../../../environment'
import {IconHeading, Section, SectionContent, SectionInfo} from '../components'

const ShowProperties = ({
  eventConfig
}: {
  eventConfig: CustomEventConfigsDetailFragment | undefined
}) => {
  const rows = Object.entries(eventConfig?.properties ?? {}).map(
    ([key, mapping]) => {
      return {id: key, type: mapping.type}
    }
  )

  return (
    <>
      <List
        rows={rows}
        columns={[
          {id: 'id', header: 'Name', accessor: v => v.id},
          {id: 'type', header: 'Type', accessor: v => v.type}
        ]}
      />
    </>
  )
}

// An overflow menu
const Overflow = ({
  item,
  closeDropdown,
  showProperties
}: {
  item: CustomEventConfigsDetailFragment
  closeDropdown: () => void
  showProperties: (id: string) => void
}) => {
  return (
    <>
      <DropdownBody>
        <>
          <DropdownItem>
            <a
              onClick={() => {
                showProperties(item.id)
                closeDropdown()
              }}
            >
              View Properties
            </a>
          </DropdownItem>
        </>
      </DropdownBody>
      <DropdownFooter>
        <OverflowFooter id={item.id} created={item.created} />
      </DropdownFooter>
    </>
  )
}

export const CustomEvents = ({environment}: {environment: EnvironmentNode}) => {
  const canView = environment.hasPermission(
    PermissionResource.AnalyticsBatch,
    PermissionAction.View
  )

  const [, loading, data] = useCustomEvents(
    environment.organisationId,
    environment.id
  )

  const [propertiesModal, setPropertiesModal] = useState<{
    open: boolean
    id?: string
  }>({open: false, id: undefined})

  if (!canView) {
    return <AccessDenied />
  }

  const columns: ListColumns<CustomEventConfigsDetailFragment> = [
    {
      id: 'name',
      header: 'Name',
      accessor: r =>
        canView ? (
          <a
            onClick={() => {
              setPropertiesModal({open: true, id: r.id})
            }}
            style={{marginRight: '-10px'}}
          >
            {r.name}
          </a>
        ) : (
          <strong style={{marginRight: '10px'}}>{r.name}</strong>
        )
    },
    {
      id: 'created',
      header: 'Created',
      accessor: row => <ElapsedAbsoluteTime date={row.created} />
    }
  ]
  const rows = data?.customEventConfigList?.nodes

  return (
    <Section>
      <Helmet>
        <title>Custom events</title>
      </Helmet>
      <header>
        <IconHeading>
          <Icon name="webhook" /> Custom events <BetaFlagMarker />
        </IconHeading>
      </header>

      <SectionInfo>
        <p>
          Custom events can be sent via Atomic's SDKs or API. This screen lists
          all the custom event types Atomic has received in the past, with their
          properties, and is is kept up to date automatically.
        </p>
      </SectionInfo>

      <SectionContent>
        {loading ? (
          <Loader />
        ) : (
          <List<CustomEventConfigsDetailFragment>
            renderEmpty={() => (
              <NoResults
                icon="webhook"
                heading="No custom events"
                message="No custom events received yet"
              />
            )}
            columns={columns}
            rows={rows}
            renderOverflow={(item, closeDropdown) => (
              <Overflow
                item={item}
                closeDropdown={closeDropdown}
                showProperties={id => {
                  setPropertiesModal({open: true, id})
                }}
              />
            )}
          ></List>
        )}
      </SectionContent>
      {propertiesModal.open && (
        <Modal
          title="Custom event properties"
          handleClose={() => setPropertiesModal({open: false, id: undefined})}
        >
          <ShowProperties
            eventConfig={rows?.find(r => r.id === propertiesModal.id)}
          />
          <ButtonGroup>
            <Button
              size="large"
              onClick={() => setPropertiesModal({open: false, id: undefined})}
            >
              Done
            </Button>
          </ButtonGroup>
        </Modal>
      )}
    </Section>
  )
}
